"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState, useEffect } from "react";
import parse from "html-react-parser";
import { ArrowRight, MapPin } from "lucide-react";
import Title from "@/core/common/Title";

const cleanHtml = (html: string) => html.replace(/<p>\s*<\/p>/g, "");

interface HolidayByMonthProps {
  packagesByMonth: any[];
}

const HolidayByMonth = ({ packagesByMonth }: HolidayByMonthProps) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!packagesByMonth.length) return;
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % packagesByMonth.length);
    }, 9000);
    return () => clearInterval(interval);
  }, [packagesByMonth.length]);

  if (!packagesByMonth?.length) return null;

  return (
    <section className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <Title
            label="Seasonal Travel"
            primaryText="Monthly"
            highlightText="Escapes"
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          <div className="w-full lg:w-1/2 flex flex-col">
            <div className="flex flex-wrap gap-x-8 gap-y-4 mb-10 border-b border-primary-200">
              {packagesByMonth.map((pkg, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`group relative py-2 transition-all duration-500 ${
                    active === i
                      ? "text-gray-900"
                      : "text-gray-300 hover:text-gray-500"
                  }`}
                >
                  <span
                    className={`text-xs block font-bold transition-all duration-700 ${active === i ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm md:text-base font-black uppercase tracking-[0.15em]">
                    {pkg.month}
                  </span>
                  <div
                    className={`absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-700 ${active === i ? "w-full" : "w-0"}`}
                  />
                </button>
              ))}
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-2 text-primary-600 font-bold uppercase text-xs tracking-[0.2em]">
                <MapPin size={16} />
                <span>{packagesByMonth[active].destination}</span>
              </div>

              <div className="overflow-hidden">
                <h3
                  key={`title-${active}`}
                  className="text-5xl md:text-6xl font-black text-gray-900 leading-[0.9] tracking-tight animate-slideUp"
                >
                  {packagesByMonth[active].name}
                </h3>
              </div>

              <div
                key={`desc-${active}`}
                className="text-gray-500 text-lg leading-relaxed line-clamp-3 animate-fadeIn"
              >
                {packagesByMonth[active].description &&
                  parse(cleanHtml(packagesByMonth[active].description))}
              </div>

              <div className="pt-4 flex flex-col gap-8">
                <Link
                  href={`/package/${packagesByMonth[active]?.slug}`}
                  className="group inline-flex items-center gap-6 bg-gray-900 text-white pl-8 pr-3 py-3 rounded-full self-start hover:bg-primary-600 transition-all duration-500"
                >
                  <span className="text-xs font-black uppercase tracking-[0.2em]">
                    View Journey
                  </span>
                  <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white group-hover:text-gray-900 transition-all">
                    <ArrowRight size={18} />
                  </div>
                </Link>

                <div className="w-full max-w-xs h-[2px] bg-gray-100 rounded-full overflow-hidden">
                  <div
                    key={`progress-${active}`}
                    className="h-full bg-primary-500 origin-left animate-progress"
                    style={{ animationDuration: "9000ms" }}
                  />
                </div>
              </div>
            </div>
          </div>

           <div className="w-full lg:w-1/2 relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
            {packagesByMonth.map((pkg, i) => (
              <div
                key={i}
                className={`absolute inset-0 transition-all duration-[3000ms] ease-in-out ${
                  i === active 
                    ? "opacity-100 scale-100 rotate-0 blur-0" 
                    : "opacity-0 scale-110 rotate-1 blur-md pointer-events-none"
                }`}
              >
                <Image
                  src={pkg?.image || "/placeholder.png"}
                  alt={pkg.month}
                  fill
                  priority={i === 0}
                  className="object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes progress {
          from {
            transform: scaleX(0);
          }
          to {
            transform: scaleX(1);
          }
        }
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
        .animate-slideUp {
          animation: slideUp 1.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .animate-fadeIn {
          animation: fadeIn 1.5s ease-out;
        }
      `}</style>
    </section>
  );
};

export default HolidayByMonth;
